// src/components/elevenlabs/ToolsPage.tsx
// STT (transcription), Audio Isolation, and Dubbing tools.

import { useRef, useState } from 'react';
import { Globe, Loader2, Mic, Upload, Wand2 } from 'lucide-react';

import {
  speechToText, isolateAudio, dubAudio, checkJob,
  type STTOut, type ELJob,
} from '@/lib/api_elevenlabs';
import { useJobPoller } from '@/hooks/useJobPoller';
import { AudioPlayer } from '@/components/shared/AudioPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';


type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function ToolsPage({ addNotification }: { addNotification?: Notifier }) {
  const [tab, setTab] = useState<'stt' | 'isolate' | 'dub'>('stt');

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit">
        {[
          { key: 'stt' as const, label: 'Transcripción' },
          { key: 'isolate' as const, label: 'Audio Isolation' },
          { key: 'dub' as const, label: 'Dubbing' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-all ${
              tab === t.key ? 'bg-[var(--acc1)] text-white shadow' : 'text-white/50 hover:text-white/80'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'stt' && <STTForm addNotification={addNotification} />}
      {tab === 'isolate' && <IsolateForm addNotification={addNotification} />}
      {tab === 'dub' && <DubForm addNotification={addNotification} />}
    </div>
  );
}

// ── STT ────────────────────────────────────────────────────────

function STTForm({ addNotification }: { addNotification?: Notifier }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [diarize, setDiarize] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<STTOut | null>(null);

  const handleSubmit = async () => {
    if (!file) return;
    setProcessing(true);
    setResult(null);
    try {
      const res = await speechToText(file, diarize);
      setResult(res);
      addNotification?.({ type: 'success', title: 'Transcripción completada' });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error en transcripción', message: e.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <FileDropZone fileRef={fileRef} file={file} onFile={setFile} accept="audio/*" />

      <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
        <input type="checkbox" checked={diarize} onChange={(e) => setDiarize(e.target.checked)} className="accent-[var(--acc1)]" />
        Diarización (identificar hablantes)
      </label>

      <button
        onClick={handleSubmit}
        disabled={processing || !file}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {processing ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
        {processing ? 'Transcribiendo…' : 'Transcribir'}
      </button>

      {result && (
        <div className="o-card border border-white/10">
          <p className="text-xs text-white/50 mb-2">Resultado</p>
          <pre className="text-sm text-white/90 whitespace-pre-wrap font-mono bg-white/5 rounded-lg p-3 max-h-80 overflow-y-auto">
            {result.text}
          </pre>
          {result.segments && result.segments.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-xs text-white/40">Segmentos</p>
              {result.segments.map((s: { start: number; end: number; text: string; speaker?: string }, i: number) => (
                <div key={i} className="text-xs text-white/60 flex gap-2">
                  <span className="text-white/30 font-mono w-20 shrink-0">
                    {s.start.toFixed(1)}s–{s.end.toFixed(1)}s
                  </span>
                  {s.speaker && <span className="text-[var(--acc2)] font-medium">{s.speaker}</span>}
                  <span>{s.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Audio Isolation ────────────────────────────────────────────

function IsolateForm({ addNotification }: { addNotification?: Notifier }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!file) return;
    setProcessing(true);
    setAudioUrl(null);
    try {
      const res = await isolateAudio(file);
      setAudioUrl(res.audio_url);
      addNotification?.({ type: 'success', title: 'Audio aislado correctamente' });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error aislando audio', message: e.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <FileDropZone fileRef={fileRef} file={file} onFile={setFile} accept="audio/*" />

      <button
        onClick={handleSubmit}
        disabled={processing || !file}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {processing ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
        {processing ? 'Procesando…' : 'Aislar voz'}
      </button>

      {audioUrl && (
        <div className="o-card border border-emerald-500/30">
          <p className="text-xs text-white/50 mb-2">Audio limpio</p>
          <AudioPlayer src={audioUrl} />
        </div>
      )}
    </div>
  );
}

// ── Dubbing ────────────────────────────────────────────────────

function DubForm({ addNotification }: { addNotification?: Notifier }) {
  const [sourceUrl, setSourceUrl] = useState('');
  const [targetLang, setTargetLang] = useState('es');
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const poller = useJobPoller<ELJob>({
    checkFn: checkJob,
    jobId,
    interval: 3000,
    onComplete: (r: ELJob) => {
      setAudioUrl(r.audio_url);
      addNotification?.({ type: 'success', title: 'Dubbing completado' });
    },
    onFail: (r: ELJob) => {
      addNotification?.({ type: 'error', title: 'Error en dubbing', message: r.error_message || 'Failed' });
    },
  });

  const handleSubmit = async () => {
    if (!sourceUrl.trim()) return;
    setSubmitting(true);
    setAudioUrl(null);
    setJobId(null);
    try {
      const job = await dubAudio({ source_url: sourceUrl.trim(), target_language: targetLang });
      setJobId(job.id);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error iniciando dubbing', message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const isPolling = poller.status === 'polling';

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-white/50 mb-1 block">URL del audio/video fuente</label>
        <input
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)]"
          placeholder="https://cdn.example.com/audio.mp3"
        />
      </div>

      <div>
        <label className="text-xs text-white/50 mb-1 block">Idioma destino</label>
        <select
          value={targetLang}
          onChange={(e) => setTargetLang(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--acc1)]"
        >
          {LANG_OPTIONS.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || isPolling || !sourceUrl.trim()}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {submitting || isPolling ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />}
        {isPolling ? 'Doblando…' : 'Iniciar dubbing'}
      </button>

      {jobId && poller.status !== 'idle' && (
        <div className="o-card flex items-center gap-3">
          <JobStatusBadge status={poller.data?.status || 'pending'} />
          <span className="text-sm text-white/60">
            {poller.status === 'polling' && 'Procesando dubbing…'}
            {poller.status === 'completed' && 'Dubbing listo'}
            {poller.status === 'failed' && (poller.error || 'Error')}
          </span>
        </div>
      )}

      {audioUrl && (
        <div className="o-card border border-emerald-500/30">
          <p className="text-xs text-white/50 mb-2">Audio doblado</p>
          <AudioPlayer src={audioUrl} />
        </div>
      )}
    </div>
  );
}

// ── Shared file drop zone ──────────────────────────────────────

function FileDropZone({
  fileRef, file, onFile, accept,
}: {
  fileRef: React.RefObject<HTMLInputElement | null>;
  file: File | null;
  onFile: (f: File | null) => void;
  accept: string;
}) {
  return (
    <div>
      <label className="text-xs text-white/50 mb-1 block">Archivo de audio</label>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-white/15 bg-white/5 text-white/50 hover:border-[var(--acc1)]/50 hover:text-white/80 transition-colors"
      >
        <Upload size={18} />
        {file ? <span className="text-sm">{file.name}</span> : <span className="text-sm">Click para seleccionar archivo</span>}
      </button>
      <input
        ref={fileRef as React.RefObject<HTMLInputElement>}
        type="file"
        accept={accept}
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        className="hidden"
      />
    </div>
  );
}

const LANG_OPTIONS = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'Inglés' },
  { code: 'pt', label: 'Portugués' },
  { code: 'fr', label: 'Francés' },
  { code: 'de', label: 'Alemán' },
  { code: 'it', label: 'Italiano' },
  { code: 'ja', label: 'Japonés' },
  { code: 'zh', label: 'Chino' },
  { code: 'ko', label: 'Coreano' },
];
